import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/utils";

export interface SettingsNavItem {
  /**
   * Âncora estável do item — vira o `id` da seção no DOM e o hash da URL.
   * Independente de `t()`: precisa continuar o mesmo depois de uma troca de
   * idioma (ver achado de review em `SettingsShell.tsx` sobre `authStore.ts`
   * chamando `i18n.changeLanguage` depois de `loadUser()` resolver, com a
   * tela já montada). `label` é só o texto exibido — nunca a identidade.
   */
  id: string;
  label: string;
  icon: ReactNode;
  danger?: boolean;
}

export interface SettingsNavGroup {
  header: string;
  items: SettingsNavItem[];
}

interface SettingsShellProps {
  groups: SettingsNavGroup[];
  /** `id` estável do item a destacar na montagem — não o `label` traduzido. */
  defaultActive?: string;
  children: ReactNode;
}

type NavPosition = readonly [groupIndex: number, itemIndex: number];

/** Onde, em `groups`, está o item com este id estável — cai no primeiro item que existe se nenhum bater. */
function findPosition(groups: SettingsNavGroup[], id: string): NavPosition {
  for (let gi = 0; gi < groups.length; gi++) {
    const ii = groups[gi].items.findIndex((item) => item.id === id);
    if (ii !== -1) return [gi, ii];
  }
  /**
   * ⚠️ Achado de review: `[0, 0]` como fallback fixo pode apontar pra uma
   * posição que não renderiza nada — um primeiro grupo vazio seguido de um
   * populado, por exemplo. Sem nenhum botão real em `[0, 0]`, `isActive`
   * fica falso pra todo mundo e **nenhum** botão ganha `tabIndex={0}`: a nav
   * inteira vira inalcançável por Tab. Cai no primeiro item que existe de
   * verdade, cruzando grupos vazios.
   */
  for (let gi = 0; gi < groups.length; gi++) {
    if (groups[gi].items.length > 0) return [gi, 0];
  }
  return [0, 0];
}

/** Hash curto e determinístico — só para o fallback de `slugify`, não para segurança. */
function simpleHash(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (Math.imul(31, hash) + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

/** id de âncora estável a partir do rótulo — acentos fora, minúsculo, traços. */
function slugify(label: string): string {
  const slug = label
    .normalize("NFD")
    // Intervalo U+0300–U+036F (marcas diacríticas combinantes) — some depois
    // do NFD separar a base do acento, ex. "Organização" → "organizacao".
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  /**
   * Rótulo sem nenhum caractere `[a-z0-9]` (só CJK, só emoji) some inteiro
   * nesse replace e vira `""` — e string vazia não é "esse item não rola",
   * é **todo** rótulo nessas condições colidindo no mesmo id vazio,
   * `getElementById("")` nunca achando nada, e a seção sumindo da
   * navegação inteira em silêncio. O hash do rótulo original garante um id
   * não-vazio e estável entre renders.
   */
  return slug || `section-${simpleHash(label)}`;
}

/** Todos os itens de nav em sequência, cruzando grupos — a ordem que scroll/teclado seguem. */
function flattenItems(
  groups: SettingsNavGroup[],
): { gi: number; ii: number; id: string; label: string }[] {
  return groups.flatMap((g, gi) =>
    g.items.map((item, ii) => ({ gi, ii, id: item.id, label: item.label })),
  );
}

export function SettingsShell({ groups, defaultActive, children }: SettingsShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();

  /**
   * ⚠️ **Ativo por posição, não por rótulo.** Rastrear `active` como o texto
   * do rótulo (`useState(first)` guardando `t("Organization")`) quebrava
   * quando a tradução chegava depois da montagem: `authStore.ts` chama
   * `i18n.changeLanguage(lang)` ao carregar o usuário, e se isso acontecer
   * com a tela já aberta, os itens re-renderizam em "Organização" enquanto
   * `active` ainda guardava "Organization" — `item.label === active` ficava
   * falso pra todos, e o destaque sumia sem nenhum clique acontecer.
   *
   * Posição no array (`[grupo, item]`) não muda quando só a tradução muda —
   * a ordem dos itens é definida pela estrutura JS de quem chama, e a
   * tradução só troca o texto de `label`. `defaultActive` recebe o `id`
   * estável do item (não o rótulo — mesmo motivo do parágrafo acima, e do
   * porquê a âncora inteira usa `id`, não `label`; ver `SettingsNavItem`).
   */
  const [active, setActive] = useState<NavPosition>(() =>
    findPosition(groups, defaultActive ?? groups[0]?.items[0]?.id ?? ""),
  );

  /**
   * "Todo item de nav leva a uma seção que existe" é a invariante que a
   * SIMP.4 (#425) travou em `admin-settings-nav.test.tsx` — mas só para
   * `AdminSettings`. `groups` e `children` são props independentes, sem nada
   * no componente ligando um ao outro, e `ProjectSettings.tsx` já viola a
   * mesma invariante (3 itens sem `FieldCard` atrás) sem teste nenhum
   * acusar: a invariante era sustentada por convenção, não por dono.
   *
   * Este aviso dá dono a ela aqui, uma vez por todo consumidor presente e
   * futuro, comparando o rótulo de cada item de nav com os `h3` de fato
   * renderizados em `children` (funciona através de wrappers como
   * `SsoCard`/`MembersCard`, que não são `FieldCard` na árvore de props mas
   * renderizam um por dentro). Dev-only e não bloqueia nada — é
   * `console.error`, não exceção, porque o item sem seção continua
   * clicável e a tela continua de pé; só o clique não leva a lugar nenhum.
   */
  const sectionsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const el = sectionsRef.current;
    if (!el) return;
    const rendered = new Set(
      Array.from(el.querySelectorAll("h3")).map((h) => h.textContent?.trim()),
    );
    const missing = groups
      .flatMap((g) => g.items.map((item) => item.label))
      .filter((label) => !rendered.has(label));
    if (missing.length > 0) {
      console.error(
        `SettingsShell: item(s) de nav sem seção renderizada correspondente: ${missing.join(", ")}. ` +
          "Todo item de nav deve levar a um FieldCard com o mesmo título, ou sair da nav.",
      );
    }

    /**
     * Achado de review: o `id` de cada seção é o `<FieldCard id>` que o
     * próprio consumidor declara (abaixo), não algo que este componente
     * carimba no DOM depois do render varrendo `children`. Sem essa ponte
     * automática, um `id` esquecido no `<FieldCard>` ou digitado diferente
     * do item de nav correspondente não vira mais um mismatch silencioso —
     * vira este aviso.
     */
    const missingIds = groups
      .flatMap((g) => g.items)
      .filter((item) => !document.getElementById(slugify(item.id)));
    if (missingIds.length > 0) {
      console.error(
        `SettingsShell: item(s) de nav com id sem seção correspondente no DOM: ${missingIds
          .map((item) => `"${item.label}" (id="${item.id}")`)
          .join(", ")}. ` +
          "O `id` do <FieldCard> precisa ser igual ao `id` do item de nav.",
      );
    }

    /**
     * Dois `id` diferentes que normalizam pra mesma âncora (`slugify`
     * descarta pontuação/acento) fazem o clique/scroll de um dos dois
     * apontar pra seção do outro — silenciosamente, porque `document.
     * getElementById` só acha o primeiro nó com aquele id. Mesmo padrão do
     * aviso acima: dev-only, não bloqueia nada.
     */
    const idsBySlug = new Map<string, Set<string>>();
    for (const item of groups.flatMap((g) => g.items)) {
      const slug = slugify(item.id);
      const ids = idsBySlug.get(slug) ?? new Set<string>();
      ids.add(item.id);
      idsBySlug.set(slug, ids);
    }
    for (const [slug, ids] of idsBySlug) {
      if (ids.size > 1) {
        console.error(
          `SettingsShell: os ids de nav ${[...ids].map((i) => `"${i}"`).join(", ")} ` +
            `geram a mesma âncora "${slug}" depois de normalizados — o clique só alcança um deles. ` +
            "Dê ids que não colidam depois de acentos/pontuação saírem.",
        );
      }
    }
  });

  /**
   * Chave estável para "os ids da nav mudaram de verdade" — `groups` chega
   * como array literal novo a cada render em todo consumidor real
   * (`AdminSettings`/`ProjectSettings` montam `groups={[...]}` inline), então
   * `[groups]` como dependência de efeito recriaria o que depender disso a
   * cada tecla digitada em qualquer campo da tela, não só quando a nav muda.
   * Comparação por conteúdo, não por referência — e por `id`, não por
   * `label`: como o `id` é estável (não depende de `t()`), trocar de idioma
   * não deveria, por si só, invalidar nada que dependa desta chave.
   */
  const navIdsKey = flattenItems(groups)
    .map((item) => item.id)
    .join(" ");

  const buttonRefs = useRef(new Map<string, HTMLButtonElement>());
  /**
   * Achado de review: um `ref={(el) => ...}` inline na JSX é uma função
   * nova a cada render — React chama o callback antigo com `null` e o novo
   * com o nó em **todo** render, mesmo o nó não tendo mudado. Como o handler
   * de keydown depende de `buttonRefs` estar populado, isso desmonta e
   * remonta cada entrada a cada tecla digitada em qualquer campo controlado
   * da tela (`Organization`/`Platform defaults` no `AdminSettings`, por
   * exemplo — nada disso depende de `SettingsShell`). Um callback por chave,
   * cacheado aqui e reaproveitado entre renders, mantém a mesma identidade
   * de função enquanto `${gi}-${ii}` não muda.
   */
  const buttonRefCallbacks = useRef(new Map<string, (el: HTMLButtonElement | null) => void>());
  const getButtonRefCallback = (key: string) => {
    let cb = buttonRefCallbacks.current.get(key);
    if (!cb) {
      cb = (el) => {
        if (el) buttonRefs.current.set(key, el);
        else buttonRefs.current.delete(key);
      };
      buttonRefCallbacks.current.set(key, cb);
    }
    return cb;
  };

  /**
   * Enquanto um scroll suave está a caminho de `pendingScrollTargetRef`, o
   * scrollspy abaixo ignora o que reporta — sem isso, cada seção que o
   * scroll atravessa no caminho até o destino dispara o destaque "andando"
   * pela lista antes de pousar onde o clique mandou. `pendingScrollTimeoutRef`
   * é só um teto de segurança: se a seção-alvo nunca ficar visível de
   * verdade, nada dispara o "chegou" e o sync ficaria suprimido pra sempre
   * sem ele.
   *
   * ⚠️ Achado de review: quem decide "chegou" é `pendingArrivalObserverRef`
   * — um `IntersectionObserver` próprio, **sem** o `rootMargin` estreito do
   * scrollspy (comentário no efeito do scrollspy, mais abaixo). Antes, quem
   * decidia era o próprio scrollspy, cuja banda ativa é só 10%–30% da
   * viewport (`rootMargin: "-10% 0px -70% 0px"`), enquanto
   * `scrollIntoView({ block: "start" })` estaciona o topo do alvo em 0% —
   * fora da banda. Uma seção curta, ou a última da nav (onde a página não
   * rola o suficiente pra empurrar o topo do alvo até lá), nunca entrava na
   * banda, o "chegou" nunca disparava, e o próximo evento do scrollspy
   * *depois* do teto de 2s destacava a seção que estivesse na banda naquele
   * instante — não a que o usuário clicou.
   */
  const pendingScrollTargetRef = useRef<string | null>(null);
  const pendingScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArrivalObserverRef = useRef<IntersectionObserver | null>(null);
  const clearPendingScroll = () => {
    pendingScrollTargetRef.current = null;
    if (pendingScrollTimeoutRef.current) {
      clearTimeout(pendingScrollTimeoutRef.current);
      pendingScrollTimeoutRef.current = null;
    }
    if (pendingArrivalObserverRef.current) {
      pendingArrivalObserverRef.current.disconnect();
      pendingArrivalObserverRef.current = null;
    }
  };
  useEffect(() => clearPendingScroll, []);

  const scrollToSection = (id: string, behavior: ScrollBehavior) => {
    const target = document.getElementById(id);
    target?.scrollIntoView({ behavior, block: "start" });
    clearPendingScroll();
    if (behavior !== "smooth" || !target) return;
    pendingScrollTargetRef.current = id;
    pendingScrollTimeoutRef.current = setTimeout(clearPendingScroll, 2000);
    if (typeof IntersectionObserver === "undefined") return;
    const arrivalObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) clearPendingScroll();
      },
      { threshold: 0 },
    );
    arrivalObserver.observe(target);
    pendingArrivalObserverRef.current = arrivalObserver;
  };

  /**
   * `push` empilha uma entrada nova (clique deliberado do mouse); `replace`
   * troca a entrada atual sem empilhar (percorrer pelo teclado, ou clicar de
   * novo no item que já está ativo). Sem essa distinção, um Home→End no nav
   * empilharia uma entrada por tecla, e sair da tela exigiria apertar voltar
   * uma vez por seção visitada em vez de uma vez para sair da tela.
   *
   * Passa por `navigate()`, não por `window.history` direto: o app inteiro
   * roda dentro de um único `BrowserRouter`, e qualquer outro leitor de
   * `useLocation()` (`RouteAnalytics`, por exemplo, que reporta pageview com
   * `location.hash`) só vê a seção mudar se a mudança passar pelo router.
   * `window.history.pushState` direto muda a barra de endereço mas deixa o
   * `location` do router — e todo mundo que lê `useLocation()` — apontando
   * pro estado antigo.
   *
   * ⚠️ Achado de review: `navigate(hash)` com uma string crua apaga a query
   * string — o `resolveTo` do react-router preenche `search` com `""`
   * quando o `to` não a inclui, e uma string só de hash não inclui nada
   * além do hash. Passar `{ hash, search: location.search, pathname:
   * location.pathname }` em vez da string crua preserva o que já estava na
   * URL. Nenhuma tela lê query param hoje, mas um `?tab=` ou `?returnTo=`
   * futuro nessas rotas morreria no primeiro clique de nav.
   */
  const lastNavigatedHashRef = useRef<string | null>(null);
  const updateHistory = (id: string, mode: "push" | "replace") => {
    const hash = `#${id}`;
    lastNavigatedHashRef.current = hash;
    navigate(
      { pathname: location.pathname, search: location.search, hash },
      { replace: mode === "replace" || location.hash === hash },
    );
  };

  const activate = (
    gi: number,
    ii: number,
    focusButton = false,
    historyMode: "push" | "replace" = "push",
  ) => {
    setActive([gi, ii]);
    const item = groups[gi]?.items?.[ii];
    if (!item) return;
    const id = slugify(item.id);
    scrollToSection(id, prefersReducedMotion ? "auto" : "smooth");
    updateHistory(id, historyMode);
    if (focusButton) buttonRefs.current.get(`${gi}-${ii}`)?.focus();
  };

  /**
   * Link direto (`/settings#billing` na montagem) e botão voltar/avançar
   * levam à mesma seção — os dois chegam como uma mudança de `location.hash`
   * que não veio de `activate()`. Reage a `location.hash` do react-router em
   * vez de escutar `popstate`/`hashchange` direto no `window`: é o sinal que
   * o próprio router já expõe pra isso, e mantém qualquer outro leitor de
   * `useLocation()` no app sincronizado com a seção — o que
   * `window.history.pushState` direto nunca faria, por não passar pelo
   * router (ver `updateHistory` acima).
   *
   * `lastNavigatedHashRef` distingue "o hash mudou porque o `activate()`
   * daqui mesmo chamou `navigate()`" de "mudou por fora" — sem isso, o
   * `navigate()` do próprio clique dispararia este efeito de novo, re-
   * rolando pra seção que o clique acabou de rolar.
   *
   * ⚠️ Achado de review: a guarda **consome** o ref ao usá-lo — sem isso, ele
   * fica escrito e nunca limpo, virando filtro permanente pra aquele valor de
   * hash. Repro: clicar em A, clicar em B, clicar em A de novo (ref trava em
   * "#a"), apertar voltar duas vezes — a 1ª volta (#a→#b) passa porque o hash
   * não bate com o ref, mas a 2ª (#b→#a) bate com o "#a" stale do terceiro
   * clique, e uma navegação de histórico genuína é engolida como se fosse o
   * eco do clique de muito antes.
   *
   * ⚠️ Achado de review: voltar para a entrada **sem** hash não pode ser
   * no-op. Repro: chega em `/admin/settings` sem hash (`active` resolve por
   * `defaultActive`), clica em Billing (empilha `#billing`), aperta Voltar —
   * a URL volta a ficar sem hash, mas nada resetava `active`/scroll, e a
   * página nunca voltava ao estado em que abriu. `isFirstSync` distingue
   * isso da montagem: se a URL já chega sem hash, o `useState` inicial já
   * resolveu `defaultActive` e não há "voltar" nenhum acontecendo.
   */
  const isFirstHashSyncRef = useRef(true);
  useEffect(() => {
    const isFirstSync = isFirstHashSyncRef.current;
    isFirstHashSyncRef.current = false;
    if (location.hash === lastNavigatedHashRef.current) {
      lastNavigatedHashRef.current = null;
      return;
    }
    const hash = location.hash.replace(/^#/, "");
    if (!hash) {
      if (isFirstSync) return;
      const fallback = findPosition(groups, defaultActive ?? groups[0]?.items[0]?.id ?? "");
      setActive(fallback);
      const fallbackItem = groups[fallback[0]]?.items?.[fallback[1]];
      if (fallbackItem) {
        scrollToSection(slugify(fallbackItem.id), prefersReducedMotion ? "auto" : "smooth");
      }
      return;
    }
    for (const item of flattenItems(groups)) {
      if (slugify(item.id) === hash) {
        setActive([item.gi, item.ii]);
        scrollToSection(hash, isFirstSync || prefersReducedMotion ? "auto" : "smooth");
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só reage a location.hash; groups mudando não deve re-disparar
  }, [location.hash]);

  /**
   * O destaque acompanha a seção visível durante o scroll manual.
   *
   * Chaveado por `navIdsKey`, não por render: `groups` é um array literal
   * novo em toda renderização de `AdminSettings`/`ProjectSettings` (digitar
   * em qualquer campo já dispara uma), e sem uma dependência estável este
   * efeito destruiria e recriaria o observer a cada tecla — sem ganho
   * nenhum, já que os elementos observados continuam os mesmos nós do DOM.
   * E por ser `id`, não `label`: trocar de idioma não deveria recriar isto
   * (o `id` de cada item não muda), só uma nav de verdade diferente deveria.
   *
   * ⚠️ Achado de review: filtra `visible` pelos ids que existem em
   * `idToPosition` **antes** do `reduce` escolher o `topMost`. Todo filho do
   * container é observado (efeito abaixo), inclusive o rodapé sticky
   * "Discard / Save changes" dos dois consumidores reais — sem `h3`, sem
   * id. Sem o filtro, esse filho podia "ganhar" o `topMost` (por ter o
   * menor `boundingClientRect.top` do lote), `idToPosition.get('')` vinha
   * `undefined`, e o `if (pos)` descartava a atualização **inteira** — o
   * destaque congelava na seção anterior mesmo com uma seção de verdade
   * também visível no mesmo lote.
   */
  const observerRef = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const idToPosition = new Map<string, NavPosition>();
    flattenItems(groups).forEach((item) => idToPosition.set(slugify(item.id), [item.gi, item.ii]));

    const observer = new IntersectionObserver(
      (entries) => {
        // "Chegou" é decidido por `pendingArrivalObserverRef` (ver
        // `scrollToSection`), não por este observer — sua banda estreita
        // não é o critério certo pra isso. Enquanto um scroll suave está
        // pendente, o scrollspy fica calado, ponto.
        if (pendingScrollTargetRef.current) return;
        const visible = entries.filter(
          (e) => e.isIntersecting && idToPosition.has((e.target as HTMLElement).id),
        );
        if (visible.length === 0) return;
        const topMost = visible.reduce((a, b) =>
          a.boundingClientRect.top <= b.boundingClientRect.top ? a : b,
        );
        const pos = idToPosition.get((topMost.target as HTMLElement).id);
        if (pos) setActive(pos);
      },
      { rootMargin: "-10% 0px -70% 0px", threshold: 0 },
    );
    observerRef.current = observer;
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
    // `navIdsKey` é deliberadamente a dependência, não `groups`: é o ponto
    // do fix, ver comentário acima de `navIdsKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navIdsKey]);

  /**
   * Achado de review: seções que montam **depois** da renderização inicial
   * (o padrão comum `{ready ? <FieldCard .../> : null}`, atrás de uma
   * query) nunca eram observadas — o efeito acima só roda quando
   * `navIdsKey` muda, e `navIdsKey` vem de `groups`, não de `children`; o
   * card pode montar sem `groups` mudar nada. Este efeito roda em toda
   * renderização (mesmo motivo do efeito de id acima) e chama `.observe()`
   * em todo filho atual — inclusive nos que já estavam observados, o que é
   * inócuo: por spec, `observe()` num alvo já observado pelo mesmo
   * observer não faz nada.
   */
  useEffect(() => {
    const el = sectionsRef.current;
    const observer = observerRef.current;
    if (!el || !observer) return;
    Array.from(el.children).forEach((section) => observer.observe(section));
  });

  const handleNavKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const items = flattenItems(groups);
    // Achado de review: sem isto, nav vazia (`groups={[]}` ou todo grupo sem
    // item) faz `items[nextIndex]` resolver `undefined`, e `next.gi` lança
    // `TypeError` dentro do handler. Nenhum consumidor atual chega nesse
    // estado, mas o handler está pendurado no `<nav>` incondicionalmente.
    if (items.length === 0) return;
    /**
     * De onde o cursor do teclado parte: o botão que **de fato** disparou o
     * evento, não `active`. Os dois divergem porque `active` também muda com
     * o scroll manual (via `IntersectionObserver`) sem tocar o foco real —
     * sem essa distinção, uma seta logo depois do scroll pularia relativo a
     * onde a página rolou, não a onde o foco está, e o botão focado
     * "teleportaria" para o próximo do item errado.
     */
    const focusedIndex = items.findIndex(
      (it) => buttonRefs.current.get(`${it.gi}-${it.ii}`) === e.target,
    );
    const currentIndex =
      focusedIndex !== -1
        ? focusedIndex
        : items.findIndex((it) => it.gi === active[0] && it.ii === active[1]);
    let nextIndex = currentIndex;
    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight":
        nextIndex = Math.min(currentIndex + 1, items.length - 1);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        nextIndex = Math.max(currentIndex - 1, 0);
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = items.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const next = items[nextIndex];
    activate(next.gi, next.ii, true, "replace");
  };

  return (
    <div className="grid grid-cols-[220px_minmax(0,1fr)] gap-6 items-start">
      {/*
        `<nav>`, não `<aside>`: isto é uma navegação para outras partes DESTA
        página (âncoras de seção), não conteúdo complementar/tangencial — e
        landmark `complementary` sem rótulo próprio também não anuncia nada
        de útil pra quem navega por landmarks.

        E não é `tablist`/`tab`: esse par implica exatamente um painel
        visível por vez, escondendo os outros — o oposto da estratégia A
        (#426), que renderiza todas as seções juntas e só rola até a
        escolhida. Marcar como aba prometeria ao leitor de tela um
        comportamento que a tela não tem.
      */}
      <nav
        aria-label={t("Settings navigation")}
        className="bg-white border border-[#e8ebf0] rounded-xl p-2.5 flex flex-col gap-0.5 sticky top-5"
        onKeyDown={handleNavKeyDown}
      >
        {groups.map((g, gi) => (
          <div key={g.header}>
            {gi > 0 && <div className="h-px bg-[#f2f4f7] my-1.5 mx-2" />}
            <div className="font-mono text-[9px] text-[#7b8799] tracking-[0.14em] uppercase px-3 pt-2.5 pb-1 font-semibold">
              {g.header}
            </div>
            {g.items.map((item, ii) => {
              const isActive = active[0] === gi && active[1] === ii;
              return (
                <button
                  key={item.id}
                  ref={getButtonRefCallback(`${gi}-${ii}`)}
                  type="button"
                  aria-current={isActive ? "true" : undefined}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => activate(gi, ii)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 text-[13px] rounded-md flex items-center gap-2.5 transition-colors",
                    isActive
                      ? "bg-[rgba(255,200,61,0.12)] text-[#08254c] font-semibold [&_svg]:text-[#ffc83d]"
                      : item.danger
                        ? "text-[#b02a31] hover:bg-[#f7f7f7] [&_svg]:text-[#b02a31]"
                        : "text-[#596b85] hover:bg-[#f7f7f7] hover:text-[#08254c] [&_svg]:text-[#7b8799]",
                  )}
                >
                  <span className="[&_svg]:w-[15px] [&_svg]:h-[15px] shrink-0 flex items-center">
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
      <div ref={sectionsRef} className="flex flex-col gap-5">
        {children}
      </div>
    </div>
  );
}

/**
 * `id` — achado de review: antes, `SettingsShell` alcançava o DOM dos
 * filhos e carimbava `section.id` depois de cada render, casando o `<h3>`
 * contra o rótulo do item de nav — três suposições frágeis embutidas (seção
 * filha direta do container, título no primeiro `h3` da subárvore, id só
 * existe depois do commit). `FieldCard` declara o próprio `id` porque já é
 * quem sabe qual é: o mesmo `id` estável do `SettingsNavItem`
 * correspondente, não o `title` (que muda com `t()` — ver o comentário
 * sobre `authStore.ts` chamando `i18n.changeLanguage` com a tela já
 * montada, mais acima neste arquivo).
 */
export function FieldCard({
  id,
  icon,
  title,
  sub,
  pill,
  right,
  children,
  danger = false,
}: {
  id?: string;
  icon?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  pill?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  danger?: boolean;
}) {
  return (
    <section
      id={id ? slugify(id) : undefined}
      className={cn(
        "bg-white border rounded-xl overflow-hidden",
        danger ? "border-[rgba(212,53,60,0.3)]" : "border-[#e8ebf0]",
      )}
    >
      <header
        className={cn(
          "px-5 py-4 border-b flex justify-between items-start gap-4",
          danger ? "bg-[rgba(212,53,60,0.04)] border-[rgba(212,53,60,0.12)]" : "border-[#f2f4f7]",
        )}
      >
        <div>
          <h3
            className={cn(
              "font-brand font-bold text-base m-0 mb-1 tracking-[-0.01em] flex items-center gap-2.5",
              danger ? "text-[#b02a31]" : "text-[#08254c]",
            )}
          >
            {icon && <span className="[&_svg]:w-4 [&_svg]:h-4 [&_svg]:text-[#ffc83d]">{icon}</span>}
            {title}
          </h3>
          {sub && <div className="text-xs text-[#596b85]">{sub}</div>}
        </div>
        {(pill || right) && <div className="flex items-center gap-2">{pill}{right}</div>}
      </header>
      <div className="px-5 py-5 flex flex-col gap-4">{children}</div>
    </section>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-5 items-start">
      <div className="text-xs font-semibold text-[#08254c] pt-2.5">
        {label}
        {hint && <div className="text-[11px] font-normal text-[#7b8799] mt-0.5">{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}
